<template>
  <div
    v-if="shouldDisplay"
    class="d-flex flex-column w-100"
    data-id-e2e="spacebreakConstraintsTable"
  >
    <div v-if="isCopyingConstraints" class="horizontal-rule" />
    <div class="spacebreak-title d-flex align-center">
      <h2>{{ isCopyingConstraints ? currentSpacebreakName : tkey('title') }}</h2>
    </div>
    <div class="d-flex flex-column overflow-hidden flex-grow-1 h-100 w-100">
      <AgGridVue
        id="spacebreak-constraints-grid"
        class="h-100 w-100"
        :column-defs="columnDefs"
        :row-data="rowData"
        auto-params-refresh
        dom-layout="autoHeight"
        :grid-options="gridOptions"
        :row-drag-managed="true"
        :animate-rows="true"
        :icons="icons"
        :header-height="isCopyingConstraints && !isGlobalSpacebreak ? 0 : 40"
        @drag-stopped="updateRanking"
        @grid-ready="onGridReady"
        @row-selected="onRowSelected"
      />
    </div>

    <!-- Table actions row -->
    <v-row v-if="!isCopyingConstraints" class="d-flex mt-3 justify-space-between">
      <div class="d-flex justify-start">
        <v-btn
          class="ml-2"
          :disabled="editingMode || isEditingDisabled"
          data-id-e2e="btnAddConstraint"
          primary
          variant="flat"
          data-dd-action-name="AddNewConstraintBtn"
          @click="addConstraint"
        >
          {{ tkey('addConstraint') }}
          <v-icon size="14" color="white" end> mdi-plus </v-icon>
        </v-btn>
        <div v-if="hasCopyConstraintsEnabled">
          <v-btn
            class="ml-2"
            :disabled="editingMode || isEditingDisabled"
            data-id-e2e="btnOpenCopyConstraintsModal"
            data-dd-action-name="CopyConstraintsBtn"
            secondary
            variant="flat"
            @click="openCopyConstraintsModal"
          >
            {{ tkey('copyConstraints') }}
            <v-icon size="14" end> $duplicate </v-icon>
          </v-btn>

          <span class="docs-link">
            <DocsLink link="toolguide/120-assortment.html#copy-constraints" />
          </span>
        </div>
      </div>
      <div class="d-flex justify-end">
        <v-btn
          variant="text"
          class="font-weight-bold remove-btn"
          data-id-e2e="btnRemoveAllConstraints"
          data-dd-action-name="RemoveAllConstraintsBtn"
          :disabled="constraintsCount === 0 || isEditingDisabled"
          @click="toggleRemoveAllConstraintModal(true)"
        >
          {{ tkey('removeAll') }}
        </v-btn>
      </div>
    </v-row>

    <!-- Remove constraints confirmation -->
    <v-dialog
      v-if="!isCopyingConstraints"
      v-model="isRemoveAllConstraintModalOpen"
      :max-width="400"
    >
      <BaseDialogCard
        :title="tkey('removeAllConfirmation.title')"
        @close="toggleRemoveAllConstraintModal(false)"
      >
        <span>
          {{ tkey('removeAllConfirmation.message', { spacebreakName }) }}
        </span>
        <template #footer>
          <v-row>
            <v-col class="d-flex justify-end">
              <v-btn
                action
                @click="[removeAllConstraints(), toggleRemoveAllConstraintModal(false)]"
              >
                {{ tkey('removeAllConfirmation.confirm') }}
              </v-btn>
              <v-btn class="ml-2" @click="toggleRemoveAllConstraintModal(false)">
                {{ tkey('removeAllConfirmation.cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </BaseDialogCard>
    </v-dialog>
  </div>
</template>

<script>
// Composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useCanvasEditability } from '@/composables/use-canvas-editability';
import { useDayjs } from '@/composables/use-dayjs';

// Stores
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';

import agGridUtils from '@/utils/ag-grid-utils';
import assortmentCanvases from '@enums/assortment-canvases';
import {
  cloneDeep,
  every,
  filter,
  find,
  first,
  get,
  isNull,
  isObject,
  isPlainObject,
  keyBy,
  map,
  merge,
  orderBy,
  sortBy,
  values,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import CellActionIconsRenderer from './renderers/CellActionIconsRenderer.vue';
import CellAppliedToRenderer from './renderers/CellAppliedToDescriptionRenderer.vue';
import CellAttributeFiltersRenderer from './renderers/CellAttributeFiltersRenderer.vue';
import CellCheckboxRenderer from './renderers/CellCheckboxRenderer.vue';
import CellDropdownRenderer from './renderers/CellDropdownRenderer.vue';
import CellExpandIconRenderer from './renderers/CellExpandIconRenderer.vue';
import CellPriorityBadgeRenderer from './renderers/CellPriorityBadgeRenderer.vue';
import CellValueInputsRenderer from './renderers/CellValueInputsRenderer.vue';

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    CellPriorityBadgeRenderer,
    CellActionIconsRenderer,
    CellExpandIconRenderer,
    CellCheckboxRenderer,
    CellDropdownRenderer,
    CellValueInputsRenderer,
    CellAttributeFiltersRenderer,
    CellAppliedToRenderer,
  },

  props: {
    spacebreak: {
      type: Object,
      default: null,
      required: false,
    },

    isCopyingConstraints: {
      type: Boolean,
      default: false,
    },

    constraints: {
      type: Object,
      default: () => ({}),
    },

    sourceScenarioAttributes: {
      type: Array,
      default: null,
      required: false,
    },

    attributesValuesInSourceScenario: {
      type: Object,
      default: () => ({}),
    },

    attributesValuesInCurrentScenario: {
      required: false,
      type: Object,
      default: () => ({}),
    },
  },

  setup() {
    const { canvasCanBeEdited } = useCanvasEditability();
    const { tkey } = useInternationalization('assortmentCanvasPage.spacebreakSettings.constraints');
    const { dayjs } = useDayjs();

    return {
      canvasCanBeEdited,
      tkey,
      dayjs,
    };
  },

  data() {
    return {
      gridOptions: null,
      gridApi: null,
      rowData: [],
      columnDefs: [
        {
          headerName: this.tkey('tableHeaders.priority'),
          field: 'priority',
          width: 100,
          minWidth: 80,
          cellRenderer: 'CellPriorityBadgeRenderer',
          cellClass: 'pl-2 align-start overflow-visible',
        },
        {
          headerName: '',
          field: 'dragIcon',
          width: 50,
          minWidth: 25,
          cellClass: 'align-start pl-1 pr-1',
          rowDrag: () => !this.isEditingDisabled,
          rowDragText: () => '',
        },
        {
          headerName: this.tkey('tableHeaders.constraintDescription'),
          field: 'constraintDescription',
          width: 500,
          minWidth: 240,
          cellRenderer: 'CellAttributeFiltersRenderer',
          cellRendererParams: {
            field: 'constraintDescription',
            sourceScenarioAttributes: this.sourceScenarioAttributes,
            attributesValuesMap: this.attributesValuesInSourceScenario,
            isCopyingConstraints: this.isCopyingConstraints,
          },
          cellClass: 'align-start overflow-visible',
        },
        {
          headerName: this.tkey('tableHeaders.parameter'),
          field: 'parameter',
          width: 300,
          minWidth: 130,
          cellRenderer: 'CellDropdownRenderer',
          cellRendererParams: {
            selectItems: [
              {
                title: this.tkey(assortmentCanvases.Constraints.AtLeast),
                value: assortmentCanvases.Constraints.AtLeast,
              },
              {
                title: this.tkey(assortmentCanvases.Constraints.Between),
                value: assortmentCanvases.Constraints.Between,
              },
              {
                title: this.tkey(assortmentCanvases.Constraints.AtMost),
                value: assortmentCanvases.Constraints.AtMost,
              },
              {
                title: this.tkey(assortmentCanvases.Constraints.EqualTo),
                value: assortmentCanvases.Constraints.EqualTo,
              },
            ],
            field: 'parameter',
          },
          cellClass: 'align-start',
        },
        {
          headerName: this.tkey('tableHeaders.value'),
          field: 'value',
          width: 210,
          minWidth: 210,
          cellRenderer: 'CellValueInputsRenderer',
          cellClass: 'align-start',
          cellRendererParams: { field: 'value' },
          valueFormatter: agGridUtils.formatters.numericFormatter,
          valueParser: agGridUtils.parsers.numericParser,
        },
        {
          headerName: this.tkey('tableHeaders.type'),
          field: 'type',
          width: 300,
          minWidth: 170,
          cellRenderer: 'CellDropdownRenderer',
          cellRendererParams: {
            selectItems: [
              {
                title: this.tkey(assortmentCanvases.Constraints.Percentage),
                value: assortmentCanvases.Constraints.Percentage,
              },
              {
                title: this.tkey(assortmentCanvases.Constraints.ProductCount),
                value: assortmentCanvases.Constraints.ProductCount,
              },
              {
                title: this.tkey(assortmentCanvases.Constraints.AbsoluteSpace),
                value: assortmentCanvases.Constraints.AbsoluteSpace,
              },
            ],
            field: 'type',
          },
          cellClass: 'align-start',
        },
        {
          headerName: this.tkey('tableHeaders.currentValue'),
          field: 'currentValue',
          width: 200,
          minWidth: 90,
          cellClass: 'align-start',
          valueFormatter: agGridUtils.formatters.numericFormatter,
        },
        {
          headerName: this.tkey('tableHeaders.appliedTo'),
          field: 'appliedTo',
          width: this.isCopyingConstraints ? 400 : 600,
          cellRenderer: 'CellAppliedToRenderer',
          cellRendererParams: {
            isCopyingConstraints: this.isCopyingConstraints,
            definitions: get(this.constraints, 'definitions'),
          },
          cellClass: this.isCopyingConstraints ? 'd-flex justify-center editing-padding-top' : '',
        },
        {
          headerName: this.$t('actions.copy'),
          field: 'toCopy',
          width: 100,
          cellRenderer: 'CellCheckboxRenderer',
          cellClass: ' icon-cell d-flex justify-center editing-padding-top',

          onCellClicked: params => this.onCheckboxCellClicked(params),
          cellRendererParams: params => {
            return {
              errors: {
                [this.$t('assortmentCanvasPage.errorMessages.attributeValueInvalid')]:
                  params.data.isAttributeValueValid,
                [this.$t('assortmentCanvasPage.errorMessages.attributeNameInvalid')]:
                  params.data.isAttributeNameValid,
              },
            };
          },
        },
        {
          headerName: '',
          field: 'icons',
          width: this.isCopyingConstraints ? 100 : 400,
          minWidth: this.isCopyingConstraints ? 100 : 140,
          cellRenderer: this.isCopyingConstraints
            ? 'CellExpandIconRenderer'
            : 'CellActionIconsRenderer',
          cellClass: 'align-start justify-end pr-2',
          // TODO: AOV3-2254 enable edition
          hide: this.isCopyingConstraints && !this.showNotImplemented,
        },
      ],
      // overriding default drag icon
      icons: { rowDrag: '<i class="fa fa-arrows"></i>' },
      isRemoveAllConstraintModalOpen: false,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'constraintEditing',
      'spacebreaks',
      'selectedCanvas',
      'canvases',
      'isConstraintSelectedToCopy',
      'selectedConstraintsToCopy',
    ]),
    ...mapState(useClusteringStore, ['getScenarioAttributes']),
    ...mapState(useContextStore, ['getClientConfig', 'showNotImplemented']),

    shouldDisplay() {
      return (
        !this.isCopyingConstraints || this.isGlobalSpacebreak || (this.rowData || []).length > 0
      );
    },

    hasCopyConstraintsEnabled() {
      return get(this.getClientConfig, 'features.copyConstraintsEnabled', false);
    },

    currentSpacebreakName() {
      return this.spacebreak ? this.spacebreak.name : this.tkey('global');
    },

    currentSpacebreakId() {
      return this.spacebreak ? this.spacebreak._id : null;
    },

    editingMode() {
      return this.constraintEditing.constraint.open;
    },

    constraintFiltersLength() {
      return get(
        this.constraintEditing,
        'constraint.rowNodeData.constraintDescription.attributeValues.length',
        0
      );
    },

    currentConstraints() {
      // When copying use the constraints from props
      if (this.isCopyingConstraints) {
        return this.constraints;
      }
      return get(this.selectedCanvas, 'constraints');
    },

    constraintsCount() {
      return this.currentConstraints
        ? filter(
            this.currentConstraints.definitions,
            c => c.spacebreakId === this.currentSpacebreakId
          ).length
        : 0;
    },

    spacebreakName() {
      if (this.spacebreak) {
        return this.spacebreak.name;
      }
      return this.tkey('globalConstraints');
    },

    isGlobalSpacebreak() {
      return isNull(this.spacebreak);
    },

    isEditingDisabled() {
      return this.isCopyingConstraints || !this.canvasCanBeEdited;
    },

    sourceScenarioAttributesById() {
      return keyBy(this.sourceScenarioAttributes, 'id');
    },

    attributesInCurrentScenarioByName() {
      return keyBy(this.getScenarioAttributes, att => {
        return att.name.toLocaleLowerCase();
      });
    },
  },

  watch: {
    spacebreak() {
      this.loadRowData();
      if (this.gridApi && this.shouldDisplay) {
        this.updateColumnsVisibility();
      }
      this.gridOptions.rowClassRules = {
        'ag-row-current-value-outside-limits': this.updateConstraintsLimitsWarning,
      };
    },

    constraintFiltersLength() {
      if (this.constraintFiltersLength > 0) {
        const calculatedRowHeight = this.calculateExpandedRowHeight(this.constraintFiltersLength);
        const currentNode = this.gridApi.getRowNode(this.constraintEditing.constraint.rowNodeId);
        currentNode.setRowHeight(calculatedRowHeight);
        this.gridApi.onRowHeightChanged();
      }
    },

    selectedCanvas() {
      this.loadRowData();
    },
  },

  async created() {
    this.gridOptions = {
      columnMenu: 'legacy',
      rowHeight: 41,
      headerHeight: 40,
      rowSelection: 'single',
      suppressContextMenu: true,
      suppressRowClickSelection: true,
      defaultColDef: {
        suppressMovable: true,
        suppressHeaderMenuButton: true,
      },
      rowClassRules: { 'ag-row-current-value-outside-limits': this.updateConstraintsLimitsWarning },
    };
  },

  mounted() {
    if (this.gridApi && this.shouldDisplay) {
      this.updateColumnsVisibility();
    }
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'fetchCanvas',
      'updateAssortmentCanvasConstraint',
      'removeAssortmentCanvasConstraintsForSpacebreak',
      'updateConstraintsRanking',
      'setConstraintEditing',
      'setConstraintRowNodeData',
      'removeSelectedConstraintsToCopyConstraint',
    ]),

    getRowData() {
      const rowData = [];
      this.gridApi.forEachNode(node => {
        rowData.push(node.data);
      });
      return rowData;
    },

    getRowNodes() {
      const rowNodes = [];
      this.gridApi.forEachNode(node => {
        rowNodes.push(node);
      });
      return rowNodes;
    },

    async removeAllConstraints() {
      // If on a spacebreak and this button was clicked, it should not remove the global constraints,
      // but only delete the spacebreak ones. When on the global spacebreak panel, then we can remove all
      const allRows = [];
      this.gridApi.forEachNodeAfterFilter(node => {
        if (this.isGlobalSpacebreak || node.data.spacebreakId === this.spacebreak._id) {
          allRows.push(node.data);
        }
      });

      // eslint-disable-next-line no-restricted-syntax,no-unused-vars
      for (const r of allRows) {
        this.gridApi.applyTransaction({ remove: [r] });
      }
      await this.removeAssortmentCanvasConstraintsForSpacebreak({
        canvasId: this.selectedCanvas._id,
        spacebreakId: this.spacebreak ? this.spacebreak._id : 'global',
      });

      await this.fetchCanvas({
        canvasId: this.selectedCanvas._id,
        forceSelectedCanvasUpdate: true,
      });
    },

    async addConstraint() {
      const newData = {
        priority: this.getRowData().length + 1,
        constraintDescription: { attributeId: '', attributeValues: [] },
        parameter: 'atLeast',
        value: [0, null],
        type: 'percentage',
        currentValue: null,
        appliedTo: this.currentSpacebreakName,
        spacebreakId: this.currentSpacebreakId,
      };
      const transaction = this.gridApi.applyTransaction({ add: [newData] });
      // We know that there will only be one entry in the transaction add array, therefore can use index 0.
      const currentNodeId = transaction.add[0].id;
      const currentNode = this.gridApi.getRowNode(currentNodeId);
      currentNode.setSelected(true);
      this.setConstraintEditing({
        rowNodeId: currentNodeId,
        editingBoolean: true,
      });
      this.setConstraintRowNodeData(newData);
      this.gridApi.redrawRows({ rowNodes: [newData] });
    },

    openCopyConstraintsModal() {
      this.$emit('switch-copy-constraints-modal', true);
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.loadRowData();
      this.updateColumnsVisibility();
      window.addEventListener('resize', () => {
        setTimeout(() => {
          params.api.sizeColumnsToFit();
        });
      });
    },

    getConstraintsForSpacebreak() {
      if (this.isGlobalSpacebreak) {
        return sortBy(
          cloneDeep(filter(this.currentConstraints.definitions, c => c.spacebreakId === null)),
          constraint => this.dayjs(constraint.createdDate)
        );
      }
      const constraintPrioritiesForspacebreak =
        this.currentConstraints.priorities[this.spacebreak._id];
      if (!constraintPrioritiesForspacebreak) return [];

      return constraintPrioritiesForspacebreak.map((constraintId, index) => {
        return merge(
          cloneDeep(find(this.currentConstraints.definitions, { id: constraintId })),
          // Priorities are based on the `priorities` array order when first loaded
          { priority: index + 1 }
        );
      });
    },

    getSpacebreakToApplyTo(constraint) {
      const appliedTo = this.spacebreak
        ? this.spacebreak.appliedTo
        : { name: this.tkey('globalConstraints'), _id: null };

      // By default constraints are applied to the spacebreak with in the same position
      // Global constraints will apply to globals by default
      // When there is no default `appliedTo` value available, the first spacebreak will be used with the copy checkbox unset. Except in the case where the constraint is a global - this is left unset so that there are not multiple constraints with the same id being copied by default

      // Example:
      // Checkpoint 1 (current)                     | Checkpoint 2 (target)
      // + SB 1                                     | + SB X
      //   - constraint1 -> applies to SB X (copy)  | + SB Y
      // + SB 2                                     |
      //   - constraint2 -> applies to SB Y (copy)  |
      // + SB 3                                     |
      //   - constraint3 -> applies to SB X unless constraint 3 is global (copy unchecked)

      if (!appliedTo && constraint.spacebreakId) {
        return first(this.spacebreaks, copySpacebreak => copySpacebreak._id);
      }

      return appliedTo;
    },

    loadRowData() {
      const constraints = map(this.getConstraintsForSpacebreak(), constraint => {
        const rowDataObject = {};
        rowDataObject.value = constraint.limits;
        if (constraint.limits[0] !== null && constraint.limits[0] === constraint.limits[1]) {
          rowDataObject.parameter = assortmentCanvases.Constraints.EqualTo;
        } else if (constraint.limits[0] !== null && constraint.limits[1] !== null) {
          rowDataObject.parameter = assortmentCanvases.Constraints.Between;
        } else if (constraint.limits[0] === null) {
          rowDataObject.parameter = assortmentCanvases.Constraints.AtMost;
        } else if (constraint.limits[1] === null) {
          rowDataObject.parameter = assortmentCanvases.Constraints.AtLeast;
        }
        rowDataObject.type = constraint.type;
        if (this.spacebreak) {
          rowDataObject.currentValue =
            isPlainObject(constraint.currentValue) && !this.isGlobalSpacebreak
              ? constraint.currentValue[this.spacebreak._id]
              : constraint.currentValue;
        }
        rowDataObject.priority = constraint.priority;
        rowDataObject.constraintDescription = constraint.attributeFilters;

        if (this.isCopyingConstraints) {
          const { canConstraintBeCopied, isAttributeNameValid, isAttributeValueValid } =
            this.isConstraintValid(rowDataObject);
          merge(rowDataObject, {
            canConstraintBeCopied,
            isAttributeNameValid,
            isAttributeValueValid,
            toCopy: canConstraintBeCopied,
            appliedTo: canConstraintBeCopied ? this.getSpacebreakToApplyTo(constraint) : null,
          });
        } else {
          rowDataObject.appliedTo = this.currentSpacebreakName;
        }
        // pre-selects checkbox
        if (this.isCopyingConstraints && rowDataObject.appliedTo) {
          this.selectedConstraintsToCopy[constraint.id] = rowDataObject.appliedTo._id;
        }

        rowDataObject.spacebreakId = constraint.spacebreakId;
        rowDataObject.constraintId = constraint.id;
        rowDataObject.isCurrentValueWithinLimits = constraint.isCurrentValueWithinLimits;
        rowDataObject.createdDate = constraint.createdDate;
        return rowDataObject;
      });
      this.rowData = orderBy(constraints, 'priority', 'asc');

      // Add editing constraint row if editing was performed before data has changed
      // $nextTick because we want initial data be rendered by ag-grid before making temporary editable row
      this.$nextTick(() => {
        const newConstraintPending =
          get(this.constraintEditing, 'constraint.open') &&
          !get(this.constraintEditing, 'constraint.rowNodeData.constraintId');

        if (newConstraintPending) {
          const data = this.constraintEditing.constraint.rowNodeData;
          data.priority = this.rowData.length + 1;
          const transaction = this.gridApi.applyTransaction({ add: [data] });
          const currentNodeId = transaction.add[0].id;
          const currentNode = this.gridApi.getRowNode(currentNodeId);
          currentNode.setSelected(true);
          this.setConstraintEditing({
            ...this.constraintEditing,
            rowNodeId: currentNodeId,
            editingBoolean: true,
          });
          this.setConstraintRowNodeData(data);
          this.gridApi.redrawRows({ rowNodes: [data] });
        }
      });
    },

    onRowSelected(event) {
      if (event.node.isSelected()) {
        const lengthOfFiltersArray = event.node.data.constraintDescription.attributeValues.length;
        const calculatedRowHeight = this.calculateExpandedRowHeight(lengthOfFiltersArray || 1);
        this.expandRowNode(event.node, calculatedRowHeight);
      }
      if (!event.node.isSelected()) {
        this.collapseRowNode(event.node);
      }
    },

    calculateExpandedRowHeight(filterCount) {
      const BASE_ROW_HEIGHT = 41; // matches gridOptions.rowHeight
      const FILTER_ROW_HEIGHT = 45; // 37px v-select + 8px margin
      const PLUS_BUTTON_BUFFER = 45; // keeps the add-value plus button visible below filter rows
      return BASE_ROW_HEIGHT + filterCount * FILTER_ROW_HEIGHT + PLUS_BUTTON_BUFFER;
    },

    expandRowNode(node, height) {
      node.setRowHeight(height);
      this.gridApi.onRowHeightChanged();
      this.gridApi.setGridOption('suppressRowDrag', true);
    },

    async collapseRowNode(node) {
      node.setRowHeight(41);
      this.gridApi.onRowHeightChanged();
      this.gridApi.setGridOption('suppressRowDrag', false);
    },

    async updateRanking() {
      // update server with new ranking
      const payload = map(this.getRowNodes(), rn => {
        const isGlobalPriority = rn.data.spacebreakId === null;

        return {
          canvasId: this.selectedCanvas._id,
          constraintId: rn.data.constraintId,
          // do not override ids for global constraints from spacebreak entry
          spacebreakId: isGlobalPriority ? null : this.spacebreak._id,
        };
      });

      await this.updateConstraintsRanking({
        canvasId: this.selectedCanvas._id,
        constraints: payload,
        spacebreakId: this.spacebreak._id,
      });
      // reload grid
      await this.fetchCanvas({
        canvasId: this.selectedCanvas._id,
        forceSelectedCanvasUpdate: true,
      });
    },

    toggleRemoveAllConstraintModal(isVisible) {
      this.isRemoveAllConstraintModalOpen = isVisible;
    },

    updateColumnsVisibility() {
      const shouldDisplayConstraintsStats = this.isCopyingConstraints || !this.isGlobalSpacebreak;
      this.gridApi.setColumnsVisible(['currentValue'], shouldDisplayConstraintsStats);
      this.gridApi.setColumnsVisible(['priority'], shouldDisplayConstraintsStats);
      this.gridApi.setColumnsVisible(['toCopy'], this.isCopyingConstraints);
      this.gridApi.sizeColumnsToFit();
    },

    updateConstraintsLimitsWarning(params) {
      const currentSpacebreakId = get(this.spacebreak, '_id', null);
      if (isNull(currentSpacebreakId)) {
        return !every(values(params.data.isCurrentValueWithinLimits));
      }
      if (isObject(params.data.isCurrentValueWithinLimits)) {
        return !get(params.data.isCurrentValueWithinLimits, currentSpacebreakId, false);
      }
      return params.data.isCurrentValueWithinLimits === false;
    },

    isConstraintValid(row) {
      /* A constraint is only valid if:
      1. The selected scenario (destination) has an attribute whose name matches the source attribute name.
      2. Given validation no. 1 passes, that for all of the attributes values used by the source constraint,
        they exist on the source constraint definition.
      * */
      const attributeNameFromSource =
        this.sourceScenarioAttributesById[
          row.constraintDescription.attributeId
        ].name.toLocaleLowerCase();

      // 1
      const attributeInCurrentScenario =
        this.attributesInCurrentScenarioByName[attributeNameFromSource];
      const isAttributeNameAvailableInCurrentScenario = !!attributeInCurrentScenario;

      const attributesValues = new Set(
        get(
          this.attributesValuesInCurrentScenario,
          [get(attributeInCurrentScenario, 'id'), 'values'],
          []
        ).map(attributeValue => attributeValue.toLocaleLowerCase())
      );

      // 2
      const isEveryAttributeValueAvailableInCurrentScenario = every(
        row.constraintDescription.attributeValues,
        attributeValueUsedInConstraint =>
          attributesValues.has(attributeValueUsedInConstraint.toLocaleLowerCase())
      );

      const canConstraintBeCopied =
        isAttributeNameAvailableInCurrentScenario &&
        isEveryAttributeValueAvailableInCurrentScenario;
      return {
        canConstraintBeCopied,
        isAttributeNameValid: isAttributeNameAvailableInCurrentScenario,
        isAttributeValueValid: isEveryAttributeValueAvailableInCurrentScenario,
      };
    },

    onCheckboxCellClicked(params) {
      const { appliedTo, constraintId, canConstraintBeCopied } = params.data;
      // TODO: AOV3-2216 ditch canConstraintBeCopied check, disabling for now constraints not 100% valid
      if (!canConstraintBeCopied || !appliedTo) return;
      const currentlySelected = this.isConstraintSelectedToCopy({
        spacebreakId: appliedTo._id,
        constraintId,
      });
      if (currentlySelected) {
        this.removeSelectedConstraintsToCopyConstraint({
          spacebreakId: appliedTo._id,
          constraintId,
        });
      } else {
        this.selectedConstraintsToCopy[constraintId] = appliedTo._id;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.fa-arrows) {
  &::before {
    font-size: 1.5rem;
    color: $assortment-primary-colour;
  }
}
:deep(.ag-center-cols-viewport) {
  overflow-x: hidden;
}
:deep(.v-input__control) {
  .v-select__slot {
    margin-left: 5px;
  }
  // hide messages box on autocomplete
  .v-text-field__details {
    display: none;
  }
}

.remove-btn {
  color: $assortment-primary-colour;
}

.spacebreak-title {
  height: 45px;
}

.horizontal-rule {
  background-color: $assortment-ag-grid-header-separator-colour;
  height: 1px;
}

:deep(.ag-row-current-value-outside-limits) {
  color: $assortment-slightly-negative-action-colour;
}
</style>
